import { axiom } from '../lib/axiom';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  User,
  Download,
  Trash2,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Shield
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

export function Settings() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleExportData = async () => {
    if (!user) return;

    setIsExporting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/user/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      const data = await response.json();

      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pistachiorama-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Your data has been exported successfully.' });
    } catch (error) {
      axiom.error('Export failed', { error: error instanceof Error ? error.message : String(error) });
      setMessage({ type: 'error', text: 'Failed to export data. Please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || deleteConfirmText !== 'DELETE') return;

    setIsDeleting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/user/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete account');
      }

      // Sign out and redirect
      await signOut();
      navigate('/signin');
    } catch (error) {
      axiom.error('Delete failed', { error: error instanceof Error ? error.message : String(error) });
      setMessage({ type: 'error', text: 'Failed to delete account. Please try again.' });
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#93c572] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-300">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <h1 className="text-3xl font-bold text-white mb-8">Account Settings</h1>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/20'
              : 'bg-red-500/10 border border-red-500/20'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            )}
            <p className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
              {message.text}
            </p>
          </div>
        )}

        {/* Profile Section */}
        <section className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-[#93c572]" />
            <h2 className="text-xl font-semibold text-white">Profile</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt=""
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#93c572]/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#93c572]" />
                </div>
              )}
              <div>
                <p className="text-white font-medium">{user.displayName || 'User'}</p>
                <p className="text-gray-400 text-sm">{user.email}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Section */}
        <section className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#93c572]" />
            <h2 className="text-xl font-semibold text-white">Privacy & Data</h2>
          </div>

          <p className="text-gray-400 mb-4">
            Under GDPR and similar privacy laws, you have the right to access, export, and delete your personal data.
          </p>

          <div className="space-y-4">
            {/* Export Data */}
            <div className="p-4 bg-[#0a0a0a] rounded-lg">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-white font-medium mb-1">Export Your Data</h3>
                  <p className="text-gray-400 text-sm">
                    Download a copy of all your personal data in JSON format.
                  </p>
                </div>
                <button
                  onClick={handleExportData}
                  disabled={isExporting}
                  className="flex items-center gap-2 px-4 py-2 bg-[#2a2a2a] hover:bg-[#333] text-white rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Export
                </button>
              </div>
            </div>

            {/* View Privacy Policy */}
            <div className="p-4 bg-[#0a0a0a] rounded-lg">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-white font-medium mb-1">Privacy Policy</h3>
                  <p className="text-gray-400 text-sm">
                    Learn how we collect, use, and protect your data.
                  </p>
                </div>
                <Link
                  to="/privacy"
                  className="flex items-center gap-2 px-4 py-2 bg-[#2a2a2a] hover:bg-[#333] text-white rounded-lg transition-colors flex-shrink-0"
                >
                  View
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="bg-[#1a1a1a] rounded-xl border border-red-500/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-xl font-semibold text-white">Danger Zone</h2>
          </div>

          <div className="p-4 bg-red-500/5 rounded-lg">
            <h3 className="text-white font-medium mb-1">Delete Account</h3>
            <p className="text-gray-400 text-sm mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>

            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Account
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-red-400 text-sm">
                  Type <strong>DELETE</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isDeleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Confirm Delete
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    className="px-4 py-2 bg-[#2a2a2a] hover:bg-[#333] text-gray-300 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Contact */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>
            Questions about your data?{' '}
            <a href="mailto:privacy@pistachiorama.ai" className="text-[#93c572] hover:underline">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
